import { Box, Tab, Tabs } from "@mui/material";

export interface FormStep<K extends string> {
  key: K;
  label: string;
  disabled?: boolean;
}

interface Props<K extends string> {
  steps: FormStep<K>[];
  value: K;
  onChange: (key: K) => void;
}

/** Numbered steps of a user or client form; the current one turns orange. */
export default function StepTabs<K extends string>({
  steps,
  value,
  onChange,
}: Props<K>) {
  return (
    <Tabs
      value={value}
      onChange={(_, next: K) => onChange(next)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        minHeight: 48,
        "& .MuiTabs-flexContainer": { justifyContent: "space-between" },
        "& .MuiTab-root": {
          flex: "1 0 auto",
          minHeight: 48,
          textTransform: "uppercase",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "0.04em",
          color: "primary.main",
        },
        "& .MuiTab-root.Mui-selected": { color: "calibrate.accentOrange" },
        "& .MuiTab-root.Mui-disabled": { color: "text.disabled" },
        "& .MuiTabs-indicator": { backgroundColor: "calibrate.accentOrange" },
      }}
    >
      {steps.map((step, index) => (
        <Tab
          key={step.key}
          value={step.key}
          disabled={step.disabled}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* The circle takes the tab's colour; only the digit is white. */}
              <Box
                component="span"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "currentColor",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{ color: "common.white", fontSize: 12, lineHeight: 1 }}
                >
                  {index + 1}
                </Box>
              </Box>
              {step.label}
            </Box>
          }
        />
      ))}
    </Tabs>
  );
}
